// src/components/events/ChooseRecipients.tsx
import React, { useState } from 'react';
import ImportRecipients from '../invitations/ImportRecipients';
import ManualEntryForm from '../invitations/ManualEntryForm';
import DeviceContactPicker from '../contacts/DeviceContactPicker';
import SparkCard from '../common/SparkCard';
import SparkButton from '../common/SparkButton';
import SparkFooter from '../common/SparkFooter';

interface ChooseRecipientsProps {
  recipients: Array<{ name: string; email?: string; phone?: string }>;
  setRecipients: React.Dispatch<
    React.SetStateAction<Array<{ name: string; email?: string; phone?: string }>>
  >;
  onNext: () => void;
  onBack: () => void;
}

const ChooseRecipients: React.FC<ChooseRecipientsProps> = ({
  recipients,
  setRecipients,
  onNext,
  onBack,
}) => {
  const [error, setError] = useState<string | null>(null);

  /* ─── Visibility Toggles ────────────────────────────────────────── */
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [showContactPicker, setShowContactPicker] = useState(false);

  /* Are we on a browser that actually supports navigator.contacts? */
  const hasContactPicker =
    typeof navigator !== 'undefined' &&
    'contacts' in navigator &&
    'ContactsManager' in window;

  /* ─── Handlers ──────────────────────────────────────────────────── */
  const handleImportComplete = (data: any[]) => {
    setRecipients((prev) => [...prev, ...data]);
    setShowCsvImport(false);
  };

  const handleAddRecipient = (recipient: {
    name: string;
    email?: string;
    phone?: string;
  }) => {
    setRecipients((prev) => [...prev, recipient]);
  };

  const handleContactsSelected = (selectedContacts: any[]) => {
    const newRecipients = selectedContacts.map((contact) => ({
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
    }));
    setRecipients((prev) => [...prev, ...newRecipients]);
    setShowContactPicker(false);
  };

  const handleContinue = () => {
    setError(null);
    onNext();
  };

  /* ─── Render ────────────────────────────────────────────────────── */
  return (
    <div className="w-full" style={{ width: '100%', boxSizing: 'border-box' }}>
      {error && <div className="error-message">{error}</div>}

      <div className="action-stack">
        {/* ── Device Contacts (Android-only) ─────────────────────── */}
        {hasContactPicker && (
          !showContactPicker ? (
            <div className="w-full">
              <SparkButton
                className="w-full"
                onClick={() => setShowContactPicker(true)}
              >
                ADD FROM CONTACTS
              </SparkButton>
            </div>
          ) : (
            <SparkCard className="spark-card--solid w-full">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-3xl font-black">Import from Device</h3>
                <SparkButton
                  size="small"
                  variant="secondary"
                  onClick={() => setShowContactPicker(false)}
                  style={{ padding: '6px 18px' }}
                >
                  HIDE
                </SparkButton>
              </div>
              <DeviceContactPicker onContactsSelected={handleContactsSelected} />
            </SparkCard>
          )
        )}

        {/* ── CSV Import ─────────────────────────────────────────── */}
        {!showCsvImport ? (
          <div className="w-full">
            <SparkButton
              className="w-full"
              onClick={() => setShowCsvImport(true)}
            >
              IMPORT FROM CSV
            </SparkButton>
          </div>
        ) : (
          <SparkCard className="spark-card--solid w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-3xl font-black">Import Recipients</h3>
              <SparkButton
                size="small"
                variant="secondary"
                onClick={() => setShowCsvImport(false)}
                style={{ padding: '6px 18px' }}
              >
                HIDE
              </SparkButton>
            </div>
            <ImportRecipients onImportComplete={handleImportComplete} />
          </SparkCard>
        )}

        {/* ── Manual Entry ───────────────────────────────────────── */}
        {!showManualEntry ? (
          <div className="w-full">
            <SparkButton
              className="w-full"
              onClick={() => setShowManualEntry(true)}
            >
              ADD MANUALLY
            </SparkButton>
          </div>
        ) : (
          <SparkCard className="spark-card--solid w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-3xl font-black">Add Recipient Manually</h3>
              <SparkButton
                size="small"
                variant="secondary"
                onClick={() => setShowManualEntry(false)}
                style={{ padding: '6px 18px' }}
              >
                HIDE
              </SparkButton>
            </div>
            <ManualEntryForm onAddRecipient={handleAddRecipient} />
          </SparkCard>
        )}
      </div>

      {/* ── Quick Preview ───────────────────────────────────────── */}
      {recipients.length > 0 && (
        <SparkCard className="spark-card--solid action-gap">
          <h4 className="text-2xl font-black mb-4">
            Current Recipients ({recipients.length})
          </h4>
          <ul className="list-disc pl-6">
            {recipients.map((r, i) => (
              <li key={i} className="text-xl mb-1">
                {r.name} — {r.email || r.phone || 'No contact info'}
              </li>
            ))}
          </ul>
        </SparkCard>
      )}

      {/* ── Navigation Buttons ──────────────────────────────────── */}
      <SparkFooter>
        <SparkButton variant="secondary" onClick={onBack}>
          BACK
        </SparkButton>
        <SparkButton onClick={handleContinue}>NEXT</SparkButton>
      </SparkFooter>
    </div>
  );
};

export default ChooseRecipients;
